import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Eye } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { UserDetailDialog } from '@/components/admin/UserDetailDialog';
import { SiteFilter } from '@/components/admin/SiteFilter';

interface CompanyUser {
  id: string;
  user_id: string;
  full_name: string;
  display_name: string;
  team: string;
  position: string;
  role: string;
  current_risk_score: number;
  credibility_score: number | null;
  site_id: string | null;
  site_name?: string;
}

interface Position {
  id: string;
  position_name: string;
}

interface Department {
  id: string;
  department_name: string;
}

interface Site {
  id: string;
  location_name: string;
}

interface AdminUsersProps {
  siteId?: string | null;
}

const AdminUsers = ({ siteId: propSiteId }: AdminUsersProps = {}) => {
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<CompanyUser[]>([]);
  const [supervisors, setSupervisors] = useState<CompanyUser[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [selectedSiteFilter, setSelectedSiteFilter] = useState<string>(propSiteId || 'all');

  // Use prop siteId if provided
  useEffect(() => {
    if (propSiteId) {
      setSelectedSiteFilter(propSiteId);
    }
  }, [propSiteId]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<CompanyUser | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    fullName: '',
    displayName: '',
    position: '',
    team: '',
    role: 'user',
    supervisorId: 'none',
    siteId: propSiteId || '',
    yearsOfExperience: 0,
  });
  const [editUser, setEditUser] = useState({
    fullName: '',
    displayName: '',
    position: '',
    team: '',
    role: 'user',
    supervisorId: 'none',
    siteId: '',
    email: '',
    password: '',
    credibilityScore: 100,
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchUsers();
    fetchSupervisors();
    fetchPositions();
    fetchDepartments();
    fetchSites();
  }, []);

  useEffect(() => {
    // Filter users when site filter changes
    if (selectedSiteFilter === 'all') {
      setFilteredUsers(users);
    } else {
      setFilteredUsers(users.filter(u => u.site_id === selectedSiteFilter));
    }
  }, [selectedSiteFilter, users]);

  const fetchSites = async () => {
    try {
      const { data, error } = await supabase
        .from('company_locations')
        .select('id, location_name')
        .eq('is_active', true)
        .order('location_name', { ascending: true });

      if (error) throw error;
      setSites(data || []);
    } catch (error: any) {
      toast({
        title: "Error loading sites",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const fetchPositions = async () => {
    try {
      const { data, error } = await supabase
        .from('positions')
        .select('id, position_name')
        .order('position_name', { ascending: true });

      if (error) throw error;
      setPositions(data || []);
    } catch (error: any) {
      toast({
        title: "Error loading positions",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const fetchDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from('departments')
        .select('id, department_name')
        .order('department_name', { ascending: true });

      if (error) throw error;
      setDepartments(data || []);
    } catch (error: any) {
      toast({
        title: "Error loading departments",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const fetchSupervisors = async () => {
    try {
      const { data, error } = await supabase
        .from('worker_profiles')
        .select('*')
        .eq('role', 'supervisor')
        .order('display_name', { ascending: true });

      if (error) throw error;
      setSupervisors(data || []);
    } catch (error: any) {
      toast({
        title: "Error loading supervisors",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('worker_profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic client-side validation to avoid unnecessary Edge Function calls
    if (!newUser.team) {
      toast({
        title: "Missing department",
        description: "Please select a Team / Department before creating the user.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Call the backend create-user function
      const { data, error } = await supabase.functions.invoke('create-user', {
        body: {
          email: newUser.email,
          password: newUser.password,
          fullName: newUser.fullName,
          displayName: newUser.displayName,
          position: newUser.position || null,
          team: newUser.team,
          role: newUser.role,
          supervisorId: newUser.supervisorId === 'none' ? null : newUser.supervisorId,
          siteId: newUser.siteId || null,
        },
      });

      if (error) {
        throw error;
      }
      
      if (!data?.success) {
        throw new Error(data?.error || 'Failed to create user');
      }

      toast({
        title: "Success",
        description: data.message || "User added successfully",
      });

      setIsDialogOpen(false);
      setNewUser({
        email: '',
        password: '',
        fullName: '',
        displayName: '',
        position: '',
        team: '',
        role: 'user',
        supervisorId: 'none',
        siteId: propSiteId || '',
        yearsOfExperience: 0,
      });
      fetchUsers();
      fetchSupervisors();
    } catch (err: any) {
      console.error('Error creating user:', err);

      let description = err?.message || 'Failed to create user';

      if (err instanceof FunctionsHttpError && err.context) {
        try {
          const serverError = await err.context.json();
          if (serverError?.error) {
            description = serverError.error;
          }
        } catch (parseError) {
          console.error('Failed to parse error response from create-user function:', parseError);
        }
      }

      toast({
        title: "Error",
        description,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    
    setIsLoading(true);

    try {
      // Call the backend update-user function
      const { data, error } = await supabase.functions.invoke('update-user', {
        body: {
          userId: editingUser.user_id,
          profileId: editingUser.id,
          fullName: editUser.fullName,
          displayName: editUser.displayName,
          position: editUser.position,
          team: editUser.team,
          role: editUser.role,
          supervisorId: editUser.supervisorId,
          siteId: editUser.siteId || null,
          credibilityScore: editUser.credibilityScore,
          email: editUser.email || undefined,
          password: editUser.password || undefined,
        },
      });

      if (error) {
        throw error;
      }
      
      if (!data?.success) {
        throw new Error(data?.error || 'Failed to update user');
      }

      toast({
        title: "Success",
        description: data.message || "User updated successfully",
      });

      setIsEditDialogOpen(false);
      setEditingUser(null);
      fetchUsers();
      fetchSupervisors();
    } catch (err: any) {
      console.error('Error updating user:', err);

      let description = err?.message || 'Failed to update user';

      if (err instanceof FunctionsHttpError && err.context) {
        try {
          const serverError = await err.context.json();
          if (serverError?.error) {
            description = serverError.error;
          }
        } catch (parseError) {
          console.error('Failed to parse error response from update-user function:', parseError);
        }
      }

      toast({
        title: "Error",
        description,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openEditDialog = (user: CompanyUser) => {
    setEditingUser(user);
    setEditUser({
      fullName: user.full_name || '',
      displayName: user.display_name,
      position: user.position || '',
      team: user.team,
      role: user.role,
      supervisorId: 'none',
      siteId: user.site_id || '',
      email: '',
      password: '',
      credibilityScore: user.credibility_score || 100,
    });
    setIsEditDialogOpen(true);
  };

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (!confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) {
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('delete-user', {
        body: { userId },
      });

      if (error) throw error;
      
      if (!data?.success) {
        throw new Error(data?.error || 'Failed to delete user');
      }

      toast({
        title: "Success",
        description: data.message || "User deleted successfully",
      });

      fetchUsers();
      fetchSupervisors();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      toast({
        title: "Error",
        description: error.message || 'Failed to delete user',
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getRiskBadge = (score: number) => {
    if (score >= 75) return <Badge variant="destructive">High Risk</Badge>;
    if (score >= 50) return <Badge className="bg-warning text-warning-foreground">Medium Risk</Badge>;
    return <Badge variant="secondary">Low Risk</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <p className="text-sm text-muted-foreground">
            Manage company users and their roles
          </p>
          {!propSiteId && (
            <SiteFilter 
              value={selectedSiteFilter} 
              onChange={setSelectedSiteFilter}
              className="w-[200px]"
            />
          )}
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New User</DialogTitle>
              <DialogDescription>Create a new user account for your company</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddUser} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  value={newUser.fullName}
                  onChange={(e) => setNewUser({ ...newUser, fullName: e.target.value })}
                  placeholder="John Doe"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="displayName">Display Name</Label>
                <Input
                  id="displayName"
                  value={newUser.displayName}
                  onChange={(e) => setNewUser({ ...newUser, displayName: e.target.value })}
                  placeholder="John"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="john@company.com"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="Min. 6 characters"
                  required
                  minLength={6}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="position">Position / Jabatan</Label>
                <Select value={newUser.position} onValueChange={(value) => setNewUser({ ...newUser, position: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select position" />
                  </SelectTrigger>
                  <SelectContent>
                    {positions.map((pos) => (
                      <SelectItem key={pos.id} value={pos.position_name}>
                        {pos.position_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="team">Team / Department</Label>
                <Select value={newUser.team} onValueChange={(value) => setNewUser({ ...newUser, team: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.department_name}>
                        {dept.department_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="supervisor">Assigned Supervisor (Optional)</Label>
                <Select value={newUser.supervisorId} onValueChange={(value) => setNewUser({ ...newUser, supervisorId: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select supervisor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Supervisor</SelectItem>
                    {supervisors.filter(s => s.team === newUser.team).map((sup) => (
                      <SelectItem key={sup.id} value={sup.id}>
                        {sup.display_name} ({sup.team})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {!propSiteId && (
                <div className="space-y-2">
                  <Label htmlFor="siteId">Assigned Site *</Label>
                  <Select value={newUser.siteId} onValueChange={(value) => setNewUser({ ...newUser, siteId: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select site" />
                    </SelectTrigger>
                    <SelectContent>
                      {sites.map((site) => (
                        <SelectItem key={site.id} value={site.id}>
                          {site.location_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="site_admin">Site Admin</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                {newUser.role === 'site_admin' && (
                  <p className="text-xs text-muted-foreground">Site admin must be assigned to sites via Sites page after creation.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="yearsOfExperience">Lama Bekerja (Tahun)</Label>
                <Input
                  id="yearsOfExperience"
                  type="number"
                  min="0"
                  value={newUser.yearsOfExperience}
                  onChange={(e) => setNewUser({ ...newUser, yearsOfExperience: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Creating..." : "Create User"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Full Name</TableHead>
              <TableHead>Display Name</TableHead>
              <TableHead>Position</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center">Loading...</TableCell>
              </TableRow>
            ) : filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  No users found{selectedSiteFilter !== 'all' ? ' for selected site' : ''}. Add your first user to get started.
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.full_name || '-'}</TableCell>
                  <TableCell>{user.display_name}</TableCell>
                  <TableCell>{user.position || '-'}</TableCell>
                  <TableCell>{user.team}</TableCell>
                  <TableCell className="capitalize">{user.role}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => { setSelectedUserId(user.id); setIsDetailOpen(true); }}>
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(user)}>Edit</Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDeleteUser(user.user_id, user.display_name)}
                        className="text-destructive hover:text-destructive"
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update user information and role</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditUser} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-fullName">Full Name</Label>
              <Input
                id="edit-fullName"
                value={editUser.fullName}
                onChange={(e) => setEditUser({ ...editUser, fullName: e.target.value })}
                placeholder="John Doe"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-displayName">Display Name</Label>
              <Input
                id="edit-displayName"
                value={editUser.displayName}
                onChange={(e) => setEditUser({ ...editUser, displayName: e.target.value })}
                placeholder="John"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email (leave blank to keep current)</Label>
              <Input
                id="edit-email"
                type="email"
                value={editUser.email}
                onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                placeholder="new-email@company.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-password">Password (leave blank to keep current)</Label>
              <Input
                id="edit-password"
                type="password"
                value={editUser.password}
                onChange={(e) => setEditUser({ ...editUser, password: e.target.value })}
                placeholder="New password (min. 6 characters)"
                minLength={6}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-credibility">Reporter Reputation / Credibility Score</Label>
              <Input
                id="edit-credibility"
                type="number"
                min="0"
                max="100"
                value={editUser.credibilityScore}
                onChange={(e) => setEditUser({ ...editUser, credibilityScore: parseInt(e.target.value) || 0 })}
              />
              <p className="text-xs text-muted-foreground">Score from 0-100. Higher = more trustworthy reports.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-position">Position / Jabatan</Label>
              <Select value={editUser.position} onValueChange={(value) => setEditUser({ ...editUser, position: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select position" />
                </SelectTrigger>
                <SelectContent>
                  {positions.map((pos) => (
                    <SelectItem key={pos.id} value={pos.position_name}>
                      {pos.position_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-team">Team / Department</Label>
              <Select value={editUser.team} onValueChange={(value) => setEditUser({ ...editUser, team: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.department_name}>
                      {dept.department_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-supervisor">Assigned Supervisor (Optional)</Label>
              <Select value={editUser.supervisorId} onValueChange={(value) => setEditUser({ ...editUser, supervisorId: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select supervisor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Supervisor</SelectItem>
                  {supervisors.filter(s => s.team === editUser.team).map((sup) => (
                    <SelectItem key={sup.id} value={sup.id}>
                      {sup.display_name} ({sup.team})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-siteId">Assigned Site</Label>
              <Select value={editUser.siteId} onValueChange={(value) => setEditUser({ ...editUser, siteId: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select site" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.location_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-role">Role</Label>
              <Select value={editUser.role} onValueChange={(value) => setEditUser({ ...editUser, role: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                  <SelectItem value="site_admin">Site Admin</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              {editUser.role === 'site_admin' && (
                <p className="text-xs text-muted-foreground">Site admin must be assigned to sites via Sites page.</p>
              )}
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "Updating..." : "Update User"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <UserDetailDialog 
        workerId={selectedUserId} 
        open={isDetailOpen} 
        onOpenChange={setIsDetailOpen} 
      />
    </div>
  );
};

export default AdminUsers;
